import { cn } from '@heroui/react'

import { type PanelRow } from '../../payload/panels'
import { n, num, s } from '../format'

export type BarSeries = { key: string; label: string; color: string }

type BarsHProps = {
  rows: PanelRow[]
  labelKey: string
  series: BarSeries[]
  label: string
  labelWidth?: number
  valueMax?: number
  strongLabels?: boolean
  /** Extra hover lines per row: [label, value]. */
  tipExtra?: (row: PanelRow) => [string, string][]
  /** A row-level "?" explanation, rendered after the label. */
  help?: (row: PanelRow) => string | null | undefined
  renderHelp?: (text: string | null | undefined) => React.ReactNode
}

/**
 * Horizontal bars, built from layout rather than drawing. Category labels are
 * words, and words read better along the left edge than rotated under an axis;
 * in CSS grid every row shares the same track widths, so bar lengths stay
 * comparable, and the value sits in its own column where it can never collide
 * with the bar. Two series render as adjacent bars, never stacked: overlapping
 * populations must not imply a sum. The hover detail is a native title: the
 * value is already printed beside the bar, so nothing is lost without it.
 * A labelled group, not an image: the labels and values are text and stay
 * readable to a screen reader row by row.
 */
const BarsH = ({
  rows,
  labelKey,
  series,
  label,
  labelWidth = 190,
  valueMax,
  strongLabels = false,
  tipExtra,
  help,
  renderHelp,
}: BarsHProps) => {
  const many = series.length > 1
  const max =
    valueMax ??
    Math.max(1, ...rows.flatMap(r => series.map(sr => n(r[sr.key]))))

  return (
    <div className="grid gap-2" role="group" aria-label={label}>
      {many && (
        <div className="flex flex-wrap justify-end gap-4">
          {series.map(sr => (
            <span
              key={sr.key}
              className="text-muted flex items-center gap-1 text-xs"
            >
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: sr.color }}
              />
              {sr.label}
            </span>
          ))}
        </div>
      )}
      <div
        className="grid items-center gap-x-3 gap-y-1.5"
        style={{
          gridTemplateColumns: `minmax(0, ${labelWidth}px) minmax(0, 1fr) auto`,
        }}
      >
        {rows.map((row, i) => {
          const text = s(row[labelKey])
          const helpText = help?.(row)
          return series.map((sr, j) => {
            const value = n(row[sr.key])
            const lines = [
              `${sr.label}: ${num(value)}`,
              ...(tipExtra?.(row) ?? []).map(([k, v]) => `${k}: ${v}`),
            ]
            return (
              <div key={`${i}-${sr.key}`} className="contents">
                <div
                  className={cn(
                    'flex items-center truncate text-sm',
                    strongLabels && 'font-semibold',
                    j > 0 && 'invisible'
                  )}
                  title={text}
                >
                  {text}
                  {j === 0 && renderHelp?.(helpText)}
                </div>
                <div
                  className={cn(
                    'bg-surface-secondary overflow-hidden rounded-sm',
                    many ? 'h-2.5' : 'h-3.5'
                  )}
                  title={lines.join(' · ')}
                >
                  <div
                    className="h-full"
                    style={{
                      width: `${(value / max) * 100}%`,
                      backgroundColor: sr.color,
                    }}
                  />
                </div>
                <div className="text-right text-sm tabular-nums">
                  {num(value)}
                </div>
              </div>
            )
          })
        })}
      </div>
    </div>
  )
}

export default BarsH
